import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { robustPost } from '../lib/robustFetch';
import { queueLead } from '../lib/leadQueue';

const HeroSection = ({ onWatchTrailer }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [region, setRegion] = useState('');
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const response = await fetch('/api/regions');
        const data = await response.json();
        setRegions(data.regions || []);
      } catch (err) {
        // Fallback: hardcoded regions if backend unreachable
        setRegions([
          'North America', 'South America', 'Europe', 'Africa',
          'Asia', 'Oceania', 'Caribbean', 'Middle East',
        ]);
      }
    };
    fetchRegions();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      email,
      whatsapp,
      region,
      gender: gender || null,
      age: age ? parseInt(age) : null,
      source: 'hero_form',
    };

    // Fire-and-forget POST: always redirect, queue if fails
    (async () => {
      try {
        const response = await robustPost('/api/lead', payload);
        if (!response.ok && response.status === 0) queueLead(payload);
      } catch {
        queueLead(payload);
      }
    })();

    // Analytics (non-blocking)
    robustPost('/api/analytics', {
      event_type: 'form_submission',
      page: '/hero',
      metadata: { source: 'hero_form', region },
    }).catch(() => {});

    // ALWAYS navigate — never lose a customer
    navigate('/thank-you');
  };

  return (
    <section 
      id="hero" 
      className="relative min-h-[90vh] flex items-center py-12 hero-gradient overflow-hidden"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Column: Headline + Form */}
          <div>
            {/* Credential Pills */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex flex-wrap gap-2 mb-6"
            >
              <span className="premium-badge">
                🎙️ BBC Presenter
              </span>
              <span className="premium-badge">
                👑 Former Miss Universe Jamaica
              </span>
              <span className="premium-badge">
                💼 Entrepreneur
              </span>
            </motion.div>

            {/* Massive Headline */}
            <motion.h1 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-6"
            >
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="text-5xl sm:text-6xl lg:text-[4rem] leading-[1.05] font-normal"
              >
                Real Lives.
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.4 }}
                className="text-5xl sm:text-6xl lg:text-[4rem] leading-[1.05] font-normal italic gradient-gold"
              >
                Real Journeys.
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.6 }}
                className="text-5xl sm:text-6xl lg:text-[4rem] leading-[1.05] font-normal"
              >
                Elsewhere.
              </motion.div>
            </motion.h1>

            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-[60ch]"
            >
              Join April Jackson as she dives into authentic conversations with global nomads, immigrants, and dream-chasers who left everything behind to build a life in a new country.
            </motion.p>

            {/* Glass Card Form */}
            <motion.form 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
              onSubmit={handleSubmit} 
              className="glass-card p-6 sm:p-8 rounded-2xl shadow-warm-lg space-y-4 max-w-md"
            >
              <div>
                <Label htmlFor="hero-email" className="text-sm font-medium mb-2 block">
                  Email Address
                </Label>
                <Input
                  id="hero-email"
                  type="text"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 rounded-xl border-border/50"
                  data-testid="hero-email-input"
                />
              </div>

              <div>
                <Label htmlFor="hero-whatsapp" className="text-sm font-medium mb-2 block">
                  WhatsApp Number
                </Label>
                <Input
                  id="hero-whatsapp"
                  type="text"
                  placeholder="+1234567890"
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  required
                  className="h-12 rounded-xl border-border/50"
                  data-testid="hero-whatsapp-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Include country code
                </p>
              </div>

              <div>
                <Label htmlFor="hero-region" className="text-sm font-medium mb-2 block">
                  Your Region (Optional)
                </Label>
                <Select value={region} onValueChange={setRegion}>
                  <SelectTrigger className="h-12 rounded-xl border-border/50" id="hero-region" data-testid="hero-region-select">
                    <SelectValue placeholder="Select your region" />
                  </SelectTrigger>
                  <SelectContent>
                    {regions.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="hero-gender" className="text-sm font-medium mb-2 block">
                  Gender (Optional)
                </Label>
                <Select value={gender} onValueChange={setGender}>
                  <SelectTrigger className="h-12 rounded-xl border-border/50" id="hero-gender" data-testid="hero-gender-select">
                    <SelectValue placeholder="Select your gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="non-binary">Non-binary</SelectItem>
                    <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="hero-age" className="text-sm font-medium mb-2 block">
                  Age (Optional)
                </Label>
                <Input
                  id="hero-age"
                  type="number"
                  placeholder="Your age"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  min="13"
                  max="120"
                  className="h-12 rounded-xl border-border/50"
                  data-testid="hero-age-input"
                />
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full h-14 rounded-full text-base font-semibold btn-premium pulse-glow"
                data-testid="hero-submit-button"
              >
                {loading ? 'Joining...' : 'Join the Community'}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                Join 2,400+ dreamers, nomads, and storytellers worldwide.
              </p>
            </motion.form>
          </div>

          {/* Right Column: Video Trailer */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <div 
              className="relative rounded-2xl overflow-hidden shadow-warm-lg group cursor-pointer border-2 border-primary/20" 
              onClick={onWatchTrailer}
            >
              <img 
                src="https://img.youtube.com/vi/iO-bL-QoK0I/maxresdefault.jpg"
                alt="Watch Trailer"
                className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Button 
                  size="lg"
                  className="rounded-full w-20 h-20 shadow-2xl group-hover:scale-110 transition-transform btn-premium pulse-ring"
                  data-testid="hero-watch-trailer-button"
                >
                  <Play className="w-8 h-8 ml-1" fill="currentColor" />
                </Button>
              </div>
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-white font-semibold text-xl mb-1">Watch the Trailer</p>
                <p className="text-white/90 text-sm">Stories of Global Reinvention</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;